// The dialogs that put a device with an organization. The two that assign it
// ask for the organization, and a device being assigned (new, or out of
// circulation) for MotionSafe's own label too; the one that approves a reset
// tablet taking its record back asks only for the code on its screen.
// Everything else they might say is on the page behind them or in the audit
// trail.

import { Alert, Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { Device } from '@motionsafe/ms-console-api/admin/v1/device_pb.js';
import type { Org } from '@motionsafe/ms-console-api/admin/v1/org_pb.js';
import { OrgStatus } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { api } from '../api/client';
import { errorText, orgsByIdQuery, useInvalidate } from '../api/hooks';
import { useOrgSearch } from './org-search';
import { useShown } from './dialog';
import { LABEL_MAX } from './device-label';
import { DeviceLine } from './device-line';
import { ICON } from '../theme';
import { DialogLoadBar } from './top-load-bar';

interface OrgChoice { value: string; label: string; room: string; disabled: boolean }

// An organization in the list: its name, and against the right edge how
// many devices it has on record.
function orgChoices(orgs: readonly Org[]): OrgChoice[] {
  return orgs.map((o) => {
    const used = o.devicesOnRecord;
    return { value: o.id, label: o.name, room: `${used} ${used === 1 ? 'device' : 'devices'}`, disabled: false };
  });
}

function OrgSelect({ choices, search, value, onChange, first = false }: { choices: OrgChoice[]; search: ReturnType<typeof useOrgSearch>['select']; value: string | null; onChange: (v: string | null) => void; /** The dialog's first field, which takes the focus as it opens. */ first?: boolean }) {
  return (
    <Select
      label="Organization"
      withAsterisk
      data={choices}
      value={value}
      onChange={onChange}
      {...search}
      allowDeselect={false}
      withCheckIcon={false}
      nothingFoundMessage="No active organization by that name"
      {...(first ? { 'data-autofocus': true } : {})}
      renderOption={({ option }) => (
        <Group flex="1" justify="space-between" wrap="nowrap" gap="md">
          <Text size="sm" inherit truncate>{option.label}</Text>
          <Text size="xs" c="dimmed" className="num" style={{ flex: 'none' }}>{(option as OrgChoice).room}</Text>
        </Group>
      )}
    />
  );
}

// MotionSafe's own label, asked for as a device is assigned. Its note and its
// length out of the limit sit under the field, as in the label dialog, so it
// reads label then input like the organization above it. Left empty, the
// device keeps the staff label it has (or, new, the one of the device it
// replaces).
function StaffLabelInput({ device, value, onChange }: { device: Device; value: string; onChange: (v: string) => void }) {
  const note = device.staffLabel ? `Now ${device.staffLabel}. The organization never sees it.` : 'MotionSafe\'s own name for it. The organization never sees it.';
  return (
    <TextInput
      label="Staff label"
      placeholder="Unit 0042"
      inputWrapperOrder={['label', 'input', 'description', 'error']}
      description={<><span>{note}</span><span className="num" style={{ flex: 'none' }}>{[...value].length} of {LABEL_MAX}</span></>}
      styles={{ description: { display: 'flex', justifyContent: 'space-between', gap: 'var(--mantine-spacing-md)', marginTop: 8 } }}
      maxLength={LABEL_MAX}
      autoComplete="off"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
}

// Each dialog stays rendered between openings so it fades both ways
// (components/dialog.ts); the form inside is keyed by the opening.
interface Opening { opened: boolean; round: number }

// Assigning a device that has just registered: which organization it is for.
type AssignNewProps = { device: Device; orgId: string; onClose: () => void };

export function AssignNewDialog({ opened, round, ...props }: AssignNewProps & Opening) {
  return (
    <Modal opened={opened} onClose={props.onClose} title="Assign device" size={440}>
      <DialogLoadBar />
      <AssignNewForm key={round} {...props} />
    </Modal>
  );
}

function AssignNewForm({ device, orgId, onClose }: AssignNewProps) {
  const inv = useInvalidate();
  const [picked, setPicked] = useState<string | null>(null);
  const [staffLabel, setStaffLabel] = useState('');
  const [error, setError] = useState('');
  // The page's organization is offered first, when it can take a device.
  const page = useQuery(orgsByIdQuery(orgId ? [orgId] : [])).data?.[0];
  const offered = page?.status === OrgStatus.ACTIVE ? page.id : null;
  const org = picked ?? offered;
  const orgs = useOrgSearch({ value: org, activeOnly: true });
  const choices = orgChoices(orgs.orgs);
  const chosen = choices.find((c) => c.value === org && !c.disabled);
  const assign = useMutation({
    mutationFn: () => api.device.assignDevice({ deviceId: device.id, orgId: org ?? '', note: '', staffLabel: staffLabel.trim() }),
    onSuccess: () => {
      notifications.show({ message: `Device assigned to ${chosen?.label ?? 'the organization'}. Its people can sign in on it.` });
      onClose();
      return inv('devices', 'org', 'orgs');
    },
    onError: (e) => setError(errorText(e)),
  });
  const ready = !!chosen && !assign.isPending;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) { setError(''); assign.mutate(); }
  };
  return (
    <form onSubmit={submit}>
      <Stack gap="lg">
        <DeviceLine device={device} />
        <OrgSelect first choices={choices} search={orgs.select} value={org} onChange={(v) => { setPicked(v); setError(''); }} />
        <StaffLabelInput device={device} value={staffLabel} onChange={(v) => { setStaffLabel(v); setError(''); }} />
        {error && <Text size="sm" c="danger" role="alert">{error}</Text>}
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!ready || assign.isPending}>Assign device</Button>
        </Group>
      </Stack>
    </form>
  );
}

// A device moved to another organization, or one out of
// circulation assigned to its next. A move is one choice, and one sentence
// on what happens to the device; an assignment asks for the staff label too.
type MoveProps = { device: Device; onClose: () => void };

export function MoveDialog({ opened, round, ...props }: MoveProps & Opening) {
  // A move or an assignment, as it was when opened: an assignment that goes
  // through gives the device an organization while the dialog fades out.
  const moving = useShown(opened ? !!props.device.orgId : null).shown ?? false;
  return (
    <Modal opened={opened} onClose={props.onClose} title={moving ? 'Move device' : 'Assign device'} size={440}>
      <DialogLoadBar />
      <MoveForm key={round} moving={moving} {...props} />
    </Modal>
  );
}

function MoveForm({ device, moving, onClose }: MoveProps & { moving: boolean }) {
  const inv = useInvalidate();
  const [org, setOrg] = useState<string | null>(null);
  const [staffLabel, setStaffLabel] = useState('');
  const [error, setError] = useState('');
  const from = useQuery(orgsByIdQuery(device.orgId ? [device.orgId] : [])).data?.[0];
  const orgs = useOrgSearch({ value: org, activeOnly: true, exclude: device.orgId });
  const choices = orgChoices(orgs.orgs);
  const chosen = choices.find((c) => c.value === org && !c.disabled);
  const move = useMutation({
    mutationFn: () => api.device.moveDevice({ deviceId: device.id, orgId: org ?? '', note: '', staffLabel: moving ? '' : staffLabel.trim() }),
    onSuccess: () => {
      notifications.show({ message: `Device assigned to ${chosen?.label ?? 'the organization'}. Its people can sign in on it from its next contact.` });
      onClose();
      return inv('devices', 'org', 'orgs', 'keys');
    },
    onError: (e) => setError(errorText(e)),
  });
  const ready = !!chosen && !move.isPending;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) { setError(''); move.mutate(); }
  };
  return (
    <form onSubmit={submit}>
      <Stack gap="lg">
        <DeviceLine device={device} from={moving ? from?.name : undefined} />
        <OrgSelect first choices={choices} search={orgs.select} value={org} onChange={(v) => { setOrg(v); setError(''); }} />
        {!moving && <StaffLabelInput device={device} value={staffLabel} onChange={(v) => { setStaffLabel(v); setError(''); }} />}
        {/* A device out of circulation was cleared when it left; only a move has something to say. */}
        {moving && <Text size="sm" c="dimmed">Everyone is signed out of it, and it clears what it holds at its next contact. Collections on its card stay{device.label ? `, and the label ${from?.name ?? 'its organization'} gave it goes` : ''}.</Text>}
        {error && (
          <Alert color="danger" role="alert" icon={<IconAlertTriangle {...ICON} />}>
            <Text size="sm">{error}</Text>
          </Alert>
        )}
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!ready || move.isPending}>{moving ? 'Move device' : 'Assign device'}</Button>
        </Group>
      </Stack>
    </form>
  );
}

// A tablet whose app was reset registered again under the device identifier
// of a device its organization has, and waits. Staff approve it with the code
// its screen shows: eight characters made from the registration's own key, so
// they tie the approval to the tablet in hand, which the device identifier
// cannot. The code it should be is never shown here: typed from the tablet,
// or it proves nothing.
type ApproveProps = { device: Device; onClose: () => void };

const CODE_LENGTH = 8;

// What was typed, as the service compares it: case, spaces and the dash between the halves do not count.
const codeCharacters = (typed: string) => typed.toUpperCase().replace(/[\s-]/g, '');

export function ApproveDialog({ opened, round, ...props }: ApproveProps & Opening) {
  return (
    <Modal opened={opened} onClose={props.onClose} title="Approve device" size={440}>
      <DialogLoadBar />
      <ApproveForm key={round} {...props} />
    </Modal>
  );
}

function ApproveForm({ device, onClose }: ApproveProps) {
  const inv = useInvalidate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const owner = useQuery(orgsByIdQuery(device.replacesOrgId ? [device.replacesOrgId] : [])).data?.[0];
  const approve = useMutation({
    mutationFn: () => api.device.approveDeviceTakeover({ deviceId: device.id, screenCode: code }),
    onSuccess: () => {
      notifications.show({ message: `Device approved. The tablet takes back its place${owner ? ` with ${owner.name}` : ''}; its record from before the reset is revoked, and everyone signed in on that one is signed out.` });
      onClose();
      return inv('devices', 'org', 'orgs');
    },
    onError: (e) => setError(errorText(e)),
  });
  const ready = codeCharacters(code).length === CODE_LENGTH && !approve.isPending;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) { setError(''); approve.mutate(); }
  };
  return (
    <form onSubmit={submit}>
      <Stack gap="lg">
        <DeviceLine device={device} from={owner?.name} ending />
        <Text size="sm">This tablet was reset and shows Device Approval Needed with a code. Nobody can sign in on it until you approve it. Approve it only with the tablet in hand, or with its code read to you by someone holding it.</Text>
        <TextInput
          label="Code on the device's screen"
          description="Eight characters, shown on the tablet's screen while it waits."
          withAsterisk
          data-autofocus
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={12}
          value={code}
          onChange={(e) => { setCode(e.currentTarget.value); setError(''); }}
          styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)', letterSpacing: '0.08em', textTransform: 'uppercase' } }}
          error={error || undefined}
        />
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!ready}>Approve device</Button>
        </Group>
      </Stack>
    </form>
  );
}

